import React, { useEffect, useState } from "react";
import { useAppSettings } from "../../../hooks/useAppSettings.tsx";
import Layout from "../../Layout.tsx";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { readBranches } from "../../../services/branch.service.ts";
import Title from "../../Title.tsx";
import { readPartnersByBranch } from "../../../services/partner.service.ts";
import moment from "moment";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { toCamelCase } from "../../../hooks/toCameCase.tsx";
import BranchSelector from "./componentes/BranchSelector.tsx";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

type Props = {};

const BirthDatesReportGeneral = (props: Props) => {
  useAppSettings();
  const [loading, setLoading] = useState<boolean>(true);
  const [currentBirthDates, setCurrentBirthDates] = useState<any[]>([]);
  const [pastBirthDates, setPastBirthDates] = useState<any[]>([]);
  const [futureBirthDates, setFutureBirthDates] = useState<any[]>([]);
  const [partnersBirthDates, setPartnersBirthDates] = useState<any[]>([]);
  const [totalPartners, setTotalPartners] = useState<any[]>([]);
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const sortByMonth = (partners: any[]) => {
    return partners.sort((a, b) => {
      const monthA = moment(a.person.birthdate).month();
      const monthB = moment(b.person.birthdate).month();
      return monthA - monthB;
    });
  };

  const fetchDependencies = async () => {
    try {
      const branches = await readBranches();
      const today = moment();

      const partnersByBranch = await Promise.all(
        branches.map(async (branch: any) => {
          const partners = await readPartnersByBranch(branch.id);
          setTotalPartners((prevTotalPartners) => [
            ...prevTotalPartners,
            ...partners,
          ]);

          const currentBd: any[] = [];
          const pastBd: any[] = [];
          const futureBd: any[] = [];

          partners.forEach((partner: any) => {
            const birthDate = moment(partner.person.birthdate);
            const currentYearBirthDate = moment(birthDate).year(today.year());

            if (today.isSame(currentYearBirthDate, "day")) {
              currentBd.push(partner);
            } else if (today.isAfter(currentYearBirthDate)) {
              pastBd.push(partner);
            } else if (today.isBefore(currentYearBirthDate)) {
              futureBd.push(partner);
            }
          });

          return {
            branch: branch,
            currentBirthDates: sortByMonth(currentBd),
            pastBirthDates: sortByMonth(pastBd),
            futureBirthDates: sortByMonth(futureBd),
          };
        })
      );

      setCurrentBirthDates(
        partnersByBranch.flatMap((p) => p.currentBirthDates)
      );
      setPastBirthDates(partnersByBranch.flatMap((p) => p.pastBirthDates));
      setFutureBirthDates(partnersByBranch.flatMap((p) => p.futureBirthDates));
      setPartnersBirthDates(partnersByBranch);
    } catch (error) {
      console.error("Error fetching dependencies:", error);
    } finally {
      setLoading(false);
    }
  };

  const isCloser = (birthdate: any) => {
    const today = moment().startOf("day");
    const days = 15;

    let birthdayThisYear = moment(birthdate).year(today.year());

    if (birthdayThisYear.isBefore(today)) {
      birthdayThisYear = birthdayThisYear.add(1, "year");
    }

    const inRange = birthdayThisYear.isBetween(
      today,
      moment(today).add(days, "days"),
      "day",
      "[]"
    );

    if (inRange) {
      const remainingDays = birthdayThisYear.diff(today, "days");
      return remainingDays;
    } else {
      return "N/A";
    }
  };

  const getNext15DaysBirthDates = (partners: any[]) => {
    return partners.filter(
      (partner: any) => isCloser(partner.person.birthdate) !== "N/A"
    );
  };

  useEffect(() => {
    fetchDependencies();
  }, []);

  return (
    <Layout loading={loading}>
      <Title
        baseTitle="Operación"
        basePath="/tactica-operativa"
        activeTitle="Cumpleaños"
        title="Reporte General de Cumpleaños"
      />
      <div className="card border-0 m-4 rounded overflow-hidden rounded-3 shadow-sm">
        <div
          className="text-white p-3 d-flex align-items-center"
          style={{
            background: "linear-gradient(245deg, #495057 0%, #212529 100%)",
          }}
        >
          <i className="fa-solid fa-filter me-2 fs-14px"></i>
          <h5
            className="mb-0"
            style={{
              fontSize: "12px",
              fontWeight: "600",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Filtros de búsqueda
          </h5>
        </div>
        <div className="p-4 bg-white">
          <BranchSelector />
        </div>
      </div>
      <div
        className="card border-0 m-4 rounded p-4 shadow-sm bg-white"
        style={{ borderRadius: "15px" }}
      >
        <div className="d-flex align-items-center mb-4 pb-2 border-bottom">
          <div
            style={{
              width: "40px",
              height: "40px",
              backgroundColor: "#50B7C020",
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: "12px",
            }}
          >
            <i
              className="fa-solid fa-chart-pie"
              style={{ color: "#50B7C0" }}
            ></i>
          </div>
          <h4 className="mb-0 fw-bold" style={{ color: "#2c3e50" }}>
            Resumen Ejecutivo
          </h4>
        </div>

        <div className="row g-4 mb-4">
          <div className="col-lg-4 col-md-12">
            <div
              className="p-3 rounded-4 h-100"
              style={{ border: "1px solid #f0f0f0", background: "#fdfdfd" }}
            >
              <h6 className="fw-bold mb-3 text-center text-muted text-uppercase fs-11px">
                Distribución Temporal
              </h6>
              <div className="d-flex justify-content-center">
                <PieChart
                  series={[
                    {
                      data: [
                        {
                          id: 0,
                          value: currentBirthDates.length,
                          label: "Hoy",
                          color: "#ff6b6b",
                        },
                        {
                          id: 1,
                          value: pastBirthDates.length,
                          label: "Pasados",
                          color: "#adb5bd",
                        },
                        {
                          id: 2,
                          value: futureBirthDates.length,
                          label: "Futuros",
                          color: "#50B7C0",
                        },
                      ],
                      innerRadius: 60,
                      paddingAngle: 5,
                      cornerRadius: 5,
                    },
                  ]}
                  width={350}
                  height={200}
                  slotProps={{
                    legend: {
                      direction: "column",
                      position: { vertical: "middle", horizontal: "right" },
                      padding: 0,
                      labelStyle: { fontSize: 12 },
                    },
                  }}
                />
              </div>
            </div>
          </div>

          <div className="col-lg-8 col-md-12">
            <div
              className="p-3 rounded-4 h-100"
              style={{
                border: "1px solid #f0f0f0",
                background: "#fdfdfd",
                overflow: "hidden",
              }}
            >
              <h6 className="fw-bold mb-3 text-center text-muted text-uppercase fs-11px">
                Cumpleaños por Sucursal
              </h6>
              <div
                className="d-flex flex-row overflow-auto pb-2"
                style={{ maxWidth: "100%" }}
              >
                {partnersBirthDates.map((p, index) => (
                  <div
                    key={index}
                    className="px-2"
                    style={{ minWidth: "250px" }}
                  >
                    <BarChart
                      xAxis={[{ scaleType: "band", data: [p.branch.name] }]}
                      series={[
                        {
                          data: [p.currentBirthDates.length],
                          label: "Hoy",
                          color: "#ff6b6b",
                        },
                        {
                          data: [p.futureBirthDates.length],
                          label: "Próximos",
                          color: "#50B7C0",
                        },
                      ]}
                      width={250}
                      height={180}
                      slotProps={{ legend: { hidden: true } }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <Box
          sx={{
            marginTop: "30px",
            "& .MuiTabs-indicator": {
              backgroundColor: "#50B7C0",
              height: "3px",
            },
            "& .MuiTab-root": {
              fontWeight: "bold",
              fontSize: "14px",
              textTransform: "none",
              color: "#6c757d",
            },
            "& .Mui-selected": { color: "#50B7C0 !important" },
          }}
        >
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="birthdates tabs"
          >
            <Tab
              icon={<i className="fa-solid fa-cake-candles me-2"></i>}
              iconPosition="start"
              label="Cumpleaños de Hoy"
              {...a11yProps(0)}
            />
            <Tab
              icon={<i className="fa-solid fa-calendar-days me-2"></i>}
              iconPosition="start"
              label="Próximos 15 días"
              {...a11yProps(1)}
            />
            <Tab
              icon={<i className="fa-solid fa-clock-rotate-left me-2"></i>}
              iconPosition="start"
              label="Historial Pasados"
              {...a11yProps(2)}
            />
          </Tabs>
        </Box>
        <CustomTabPanel value={value} index={0}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
              gap: "20px",
              marginTop: "10px",
            }}
          >
            {partnersBirthDates
              .filter((p) => p.currentBirthDates.length > 0)
              .map((p, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: "white",
                  borderRadius: "16px",
                  border: "1px solid #f0f0f0",
                  overflow: "hidden",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                }}
              >
                <div
                  style={{
                    padding: "12px 20px",
                    backgroundColor: "#f8f9fa",
                    borderBottom: "1px solid #eee",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span className="fw-bold" style={{ color: "#495057" }}>
                    {p.branch.name}
                  </span>
                  <span
                    className="badge rounded-pill"
                    style={{ backgroundColor: "#ff6b6b", fontSize: "10px" }}
                  >
                    {p.currentBirthDates.length} hoy
                  </span>
                </div>
                <div style={{ padding: "0px" }}>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#fff" }}>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: "600",
                            color: "#888",
                            fontSize: "11px",
                            textTransform: "uppercase",
                          }}
                        >
                          Colaborador
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: "600",
                            color: "#888",
                            fontSize: "11px",
                            textTransform: "uppercase",
                          }}
                        >
                          Edad
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {p.currentBirthDates.length > 0 ? (
                        p.currentBirthDates.map(
                          (partner: any, partnerIndex: number) => (
                            <TableRow key={partnerIndex} hover>
                              <TableCell
                                sx={{ borderBottom: "1px solid #f9f9f9" }}
                              >
                                <div className="d-flex align-items-center">
                                  <div
                                    style={{
                                      width: "32px",
                                      height: "32px",
                                      borderRadius: "50%",
                                      backgroundColor: "#ff6b6b20",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      marginRight: "10px",
                                      color: "#ff6b6b",
                                      fontSize: "11px",
                                    }}
                                  >
                                    {partner.person.firstName[0]}
                                    {partner.person.lastName[0]}
                                  </div>
                                  <span
                                    style={{
                                      fontSize: "13px",
                                      fontWeight: "500",
                                    }}
                                  >
                                    {toCamelCase(
                                      `${partner.person.firstName} ${partner.person.lastName}`
                                    )}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  borderBottom: "1px solid #f9f9f9",
                                  fontSize: "13px",
                                  fontWeight: "bold",
                                  color: "#444",
                                }}
                              >
                                {moment().diff(
                                  moment(partner.person.birthdate),
                                  "years"
                                )}{" "}
                                años
                              </TableCell>
                            </TableRow>
                          )
                        )
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={2}
                            align="center"
                            sx={{
                              py: 3,
                              color: "#adb5bd",
                              fontSize: "12px",
                              fontStyle: "italic",
                            }}
                          >
                            Sin cumpleaños el día de hoy
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ))}
            {partnersBirthDates.filter((p) => p.currentBirthDates.length > 0).length === 0 && (
              <div
                style={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  padding: "48px 16px",
                  color: "#adb5bd",
                  fontSize: "14px",
                  fontStyle: "italic",
                }}
              >
                Sin cumpleaños el día de hoy en ninguna sucursal
              </div>
            )}
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
              gap: "20px",
              marginTop: "10px",
            }}
          >
            {partnersBirthDates
              .map((p) => ({
                ...p,
                next15DaysBirthDates: getNext15DaysBirthDates(p.futureBirthDates),
              }))
              .filter((p) => p.next15DaysBirthDates.length > 0)
              .map((p, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: "white",
                  borderRadius: "16px",
                  border: "1px solid #f0f0f0",
                  overflow: "hidden",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                }}
              >
                <div
                  style={{
                    padding: "12px 20px",
                    backgroundColor: "#f8f9fa",
                    borderBottom: "1px solid #eee",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span className="fw-bold" style={{ color: "#495057" }}>
                    {p.branch.name}
                  </span>
                  <span
                    className="badge rounded-pill"
                    style={{ backgroundColor: "#50B7C0", fontSize: "10px" }}
                  >
                    {p.next15DaysBirthDates.length} próximos 15 días
                  </span>
                </div>
                <div>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#fff" }}>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: "600",
                            color: "#888",
                            fontSize: "11px",
                            textTransform: "uppercase",
                          }}
                        >
                          Colaborador
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: "600",
                            color: "#888",
                            fontSize: "11px",
                            textTransform: "uppercase",
                          }}
                        >
                          Fecha / Días
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {p.next15DaysBirthDates.length > 0 ? (
                        p.next15DaysBirthDates.map(
                          (partner: any, partnerIndex: number) => (
                            <TableRow key={partnerIndex} hover>
                              <TableCell
                                sx={{ borderBottom: "1px solid #f9f9f9" }}
                              >
                                <div
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: "500",
                                  }}
                                >
                                  {toCamelCase(
                                    `${partner.person.firstName} ${partner.person.lastName}`
                                  )}
                                </div>
                                <div
                                  style={{ fontSize: "10px", color: "#999" }}
                                >
                                  Cumple{" "}
                                  {moment().diff(
                                    moment(partner.person.birthdate),
                                    "years"
                                  ) + 1}{" "}
                                  años
                                </div>
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ borderBottom: "1px solid #f9f9f9" }}
                              >
                                <div
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: "bold",
                                    color: "#2c3e50",
                                  }}
                                >
                                  {moment(partner.person.birthdate).format(
                                    "DD MMM"
                                  )}
                                </div>
                                <div style={{ fontSize: "10px" }}>
                                  <span
                                    className={`badge ${
                                      Number(
                                        isCloser(partner.person.birthdate)
                                      ) <= 3
                                        ? "bg-danger"
                                        : "bg-light text-dark border"
                                    }`}
                                  >
                                    En {isCloser(partner.person.birthdate)} días
                                  </span>
                                </div>
                              </TableCell>
                            </TableRow>
                          )
                        )
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={2}
                            align="center"
                            sx={{
                              py: 3,
                              color: "#adb5bd",
                              fontSize: "12px",
                              fontStyle: "italic",
                            }}
                          >
                            No hay cumpleaños en los próximos 15 días
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ))}
            {partnersBirthDates
              .map((p) => ({
                ...p,
                next15DaysBirthDates: getNext15DaysBirthDates(p.futureBirthDates),
              }))
              .filter((p) => p.next15DaysBirthDates.length > 0).length === 0 && (
              <div
                style={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  padding: "48px 16px",
                  color: "#adb5bd",
                  fontSize: "14px",
                  fontStyle: "italic",
                }}
              >
                No hay cumpleaños en los próximos 15 días en ninguna sucursal
              </div>
            )}
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
              gap: "20px",
              marginTop: "10px",
            }}
          >
            {partnersBirthDates
              .filter((p) => p.pastBirthDates.length > 0)
              .map((p, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: "white",
                  borderRadius: "16px",
                  border: "1px solid #f0f0f0",
                  overflow: "hidden",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                }}
              >
                <div
                  style={{
                    padding: "12px 20px",
                    backgroundColor: "#f8f9fa",
                    borderBottom: "1px solid #eee",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span className="fw-bold" style={{ color: "#495057" }}>
                    {p.branch.name}
                  </span>
                  <span
                    className="badge rounded-pill"
                    style={{ backgroundColor: "#adb5bd", fontSize: "10px" }}
                  >
                    {p.pastBirthDates.length} pasados
                  </span>
                </div>
                <div>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#fff" }}>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: "600",
                            color: "#888",
                            fontSize: "11px",
                            textTransform: "uppercase",
                          }}
                        >
                          Colaborador
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: "600",
                            color: "#888",
                            fontSize: "11px",
                            textTransform: "uppercase",
                          }}
                        >
                          Fecha de Nac.
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {p.pastBirthDates.length > 0 ? (
                        p.pastBirthDates.map(
                          (partner: any, partnerIndex: number) => (
                            <TableRow key={partnerIndex} hover>
                              <TableCell
                                sx={{ borderBottom: "1px solid #f9f9f9" }}
                              >
                                <span
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: "500",
                                  }}
                                >
                                  {toCamelCase(
                                    `${partner.person.firstName} ${partner.person.lastName}`
                                  )}
                                </span>
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  borderBottom: "1px solid #f9f9f9",
                                  fontSize: "12px",
                                  color: "#666",
                                }}
                              >
                                {moment(partner.person.birthdate).format(
                                  "DD/MM/YYYY"
                                )}
                              </TableCell>
                            </TableRow>
                          )
                        )
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={2}
                            align="center"
                            sx={{
                              py: 3,
                              color: "#adb5bd",
                              fontSize: "12px",
                              fontStyle: "italic",
                            }}
                          >
                            Sin historial de cumpleaños pasados
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ))}
            {partnersBirthDates.filter((p) => p.pastBirthDates.length > 0).length === 0 && (
              <div
                style={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  padding: "48px 16px",
                  color: "#adb5bd",
                  fontSize: "14px",
                  fontStyle: "italic",
                }}
              >
                No hay historial de cumpleaños pasados en ninguna sucursal
              </div>
            )}
          </div>
        </CustomTabPanel>
      </div>
    </Layout>
  );
};

export default BirthDatesReportGeneral;
